import { useQuery } from '@apollo/client';
import { isEmpty, isNil } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useId, useMemo, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce/lib';
import { Queries } from '@shared/api';
import {
  useControlsActions,
  useControlsState,
  useGroupContext,
  useViewTemplateState,
} from '@shared/components/filter/state';
import {
  getFilterScalarKey,
  getFilterValueType,
} from '@shared/components/filter/state/filterScalarKey';
import {
  FilterDateOperator,
  FilterDateSelection,
  FilterNumberSelection,
  FilterPersonSelection,
  FilterSelectionState,
  FilterSelectionStateGroup,
  FilterTagSelection,
  FilterTextSelection,
  SortOption,
  SortSelectionState,
  isFilterBooleanSelection,
} from '@shared/components/filter/types';
import type { Entity } from '@shared/data/entity';
import { API } from '@shared/types/api';
import { User } from '@shared/types/user';
import { useProfile } from '@shared/util/hooks';
import { notNil } from '@shared/util/notNil';
import { usePermission } from '@shared/util/permissions';
import { CardEntity } from './CardEntity';
import { useLoadMore } from './hooks/useLoadMore';
import { CARDS_PER_GROUP_PAGE, CARDS_PER_PAGE, useUpdateList } from './hooks/useUpdateList';

export interface CardsEntityServiceProps {
  workflowTemplateId: string;
  viewTemplateId: string;
  workspaceId: number;
  pageSize?: number;
  dateComponentId?: string;
  // for tree views, the related-card component defining the hierarchy; requests
  // per-card child counts alongside the entities and scopes the query to one
  // level of the tree (see treeParentCardId)
  treeParentComponentId?: string | null;
  // for tree views, the card whose children to fetch; when unset, fetches root
  // cards (those with no parent value)
  treeParentCardId?: string;
}

export interface CardsEntityService {
  entities: Entity[];
  entityVariables: API.ListWorkflowEntitiesQueryVariables;
  totalCardCount: number;
  // per-card child counts, keyed by card id; sparse — cards with zero children
  // are omitted. Only populated when treeParentComponentId is set.
  childCardCounts: Map<string, number>;
  isCardsLoading: boolean;
  isMoreCardsLoading: boolean;
  canUpdateCard: boolean;
  onSearch(keyword: string): void;
  onReorder(componentId: string, order: string): void;
  fetchMore(): void;
  refetch(): void;
  hasMoreCards: boolean;
  errors: Error[];
}

export function useCardService({
  workflowTemplateId,
  viewTemplateId,
  workspaceId,
  pageSize,
  dateComponentId,
  treeParentComponentId,
  treeParentCardId,
}: CardsEntityServiceProps): CardsEntityService {
  const { sorts, filters, availableSorts, search } = useControlsState();
  const group = useGroupContext();
  const { viewTemplate } = useViewTemplateState();

  return useCardServiceQuery({
    workflowTemplateId,
    viewTemplateId,
    workspaceId,
    pageSize,
    sorts,
    filters,
    availableSorts,
    search,
    group,
    viewTemplate,
    dateComponentId,
    treeParentComponentId,
    treeParentCardId,
  });
}

export interface CardServiceQueryProps extends CardsEntityServiceProps {
  sorts: Map<string, SortSelectionState>;
  filters: Map<string, FilterSelectionState>;
  availableSorts: SortOption[];
  search: string | null;
  group: FilterSelectionStateGroup | null;
  viewTemplate: API.ViewTemplateFragment | undefined;
  dateComponentId?: string;
}

export function useCardServiceQuery({
  workflowTemplateId,
  viewTemplateId,
  workspaceId,
  pageSize,
  sorts: selectedSortOptions,
  filters: selectedFilterOptions,
  availableSorts,
  search,
  group,
  viewTemplate,
  dateComponentId,
  treeParentComponentId,
  treeParentCardId,
}: CardServiceQueryProps): CardsEntityService {
  const isGroup = !isNil(group);
  const canUpdateCard = usePermission({
    action: 'update',
    subject: 'Card',
    channelId: workspaceId,
  }).enabled;

  const sortByComponents = useMemo(() => {
    return Array.from(selectedSortOptions.values()).map(getSortComponent);
  }, [selectedSortOptions]);

  const filterByComponents = useMemo(() => {
    const selected = new Map(selectedFilterOptions);
    if (isGroup) {
      Array.from(group.entries()).forEach(([id, filter]) => {
        selected.set(id, filter);
      });
    }

    const converted = Array.from(selected.values())
      .map(v => getFilterComponent({ ...v, prepareForMutation: false }))
      .filter(notNil);
    // tree views scope each query to one level of the hierarchy: children of
    // treeParentCardId, or root cards (no parent value) when it is unset
    if (!isNil(treeParentComponentId)) {
      converted.push({
        componentId: treeParentComponentId,
        value: {
          containsString: isNil(treeParentCardId)
            ? null
            : { values: [treeParentCardId], negate: false },
          isEmpty: isNil(treeParentCardId) ? { value: true } : null,
          stringWildcard: null,
          containsNumber: null,
          dateIs: null,
          dateRange: null,
          dateTokenIs: null,
          numberIs: null,
          containsCurrentUser: null,
        },
      });
    }
    return converted;
  }, [selectedFilterOptions, group, isGroup, treeParentComponentId, treeParentCardId]);

  const entityVariables: API.ListWorkflowEntitiesQueryVariables = useMemo(() => {
    const sortByCreatedAt: API.SortByComponent = {
      componentId: 'createdAt',
      direction: 'DESC',
    };

    const hasCreatedAtSort = availableSorts.some(item => item.id === 'createdAt');
    const customSortingEnabled = !!viewTemplate?.customSortingEnabled;
    const shouldUseCreatedAtAsDefaultSort = hasCreatedAtSort && !customSortingEnabled;
    const defaultSort = shouldUseCreatedAtAsDefaultSort ? [sortByCreatedAt] : [];

    const userSort = !isEmpty(sortByComponents) ? sortByComponents : defaultSort;

    // for calendar views, add the view's date component id as the primary sort
    // so that cards appear in the right order when navigating left/right in the
    // card modal
    const dateComponentSort: API.SortByComponent[] = dateComponentId
      ? [{ componentId: dateComponentId, direction: 'ASC' }]
      : [];

    const defaultPageSize = isGroup ? CARDS_PER_GROUP_PAGE : CARDS_PER_PAGE;

    // added conditionally so that non-tree query variables (and thus Apollo
    // cache keys) are unchanged
    const inverseCountsInput = treeParentComponentId
      ? { inverseRelatedCardCountsForComponentId: treeParentComponentId }
      : {};

    return {
      input: {
        workflowTemplateId,
        viewTemplateId,
        limit: pageSize ?? defaultPageSize,
        cursor: '',
        sortByComponents: [...dateComponentSort, ...userSort],
        filterByComponents,
        omniSearchQuery: search,
        ...inverseCountsInput,
      },
    };
  }, [
    availableSorts,
    dateComponentId,
    filterByComponents,
    isGroup,
    pageSize,
    search,
    sortByComponents,
    treeParentComponentId,
    workflowTemplateId,
    viewTemplateId,
    viewTemplate?.customSortingEnabled,
  ]);

  const {
    data,
    previousData,
    loading: isEntitiesLoading,
    fetchMore: entityFetchMore,
    refetch,
  } = useQuery<API.ListWorkflowEntitiesQuery, API.ListWorkflowEntitiesQueryVariables>(
    Queries.workflow.listWorkflowEntities,
    {
      variables: entityVariables,
      skip: isNil(workflowTemplateId),
      fetchPolicy: 'cache-and-network',
      // After the initial network fetch, switch to cache-first so that
      // subscription-driven writeQuery calls don't each trigger a new
      // network request. The subscription keeps the cache up to date.
      nextFetchPolicy: 'cache-first',
    },
  );

  // A filter or sort edit changes the variables; until the new result arrives
  // Apollo has no data for them, so fall back to the last one to keep rows on
  // screen. Scoped by collection because `previousData` belongs to the hook and
  // survives navigating to another workflow or view template.
  const collectionKey = `${workflowTemplateId}:${viewTemplateId}:${treeParentCardId ?? ''}`;
  const loadedCollectionKey = useRef<string | null>(null);
  useEffect(() => {
    if (!isNil(data)) {
      loadedCollectionKey.current = collectionKey;
    }
  }, [data, collectionKey]);

  const listEntities =
    data ?? (loadedCollectionKey.current === collectionKey ? previousData : undefined);

  const { isLoadingMore, loadMore } = useLoadMore(entityFetchMore, listEntities, entityVariables);
  const hasMoreCards = listEntities?.list.hasMore ?? false;

  const [fetchMoreCards] = useDebouncedCallback(
    useCallback(() => {
      // mid-refetch `listEntities` is the previous result, so is its cursor
      if (isLoadingMore || !hasMoreCards || isEntitiesLoading) {
        return;
      }

      loadMore();
    }, [isLoadingMore, loadMore, hasMoreCards, isEntitiesLoading]),
    100,
  );

  // publishes this query's in-flight state so the collection controls can show a
  // single indicator covering every query the view runs
  const { setLoading } = useControlsActions();
  const queryId = useId();
  useEffect(() => {
    setLoading(queryId, isEntitiesLoading || isLoadingMore);
    return () => setLoading(queryId, false);
  }, [setLoading, queryId, isEntitiesLoading, isLoadingMore]);

  // the tree level filter is injected into the query variables (not the
  // controls state), so subscription-driven cache updates need this predicate
  // to keep created/updated cards out of the other tree levels' lists
  const cardMatchesTreeLevel = useCallback(
    (entity: API.WorkflowEntity) => {
      if (isNil(treeParentComponentId)) {
        return true;
      }
      const fieldValue = entity.fields?.[treeParentComponentId];
      if (isNil(treeParentCardId)) {
        return isEmpty(fieldValue);
      }
      return !isNil(fieldValue?.find?.((item: { id?: string }) => item?.id === treeParentCardId));
    },
    [treeParentComponentId, treeParentCardId],
  );

  useUpdateList(entityVariables, {
    cardMatchesList: isNil(treeParentComponentId) ? undefined : cardMatchesTreeLevel,
  });
  const { profile } = useProfile();

  const entities = useMemo(
    () => getEntities({ listEntities, workflowTemplateId, viewTemplate, currentUser: profile }),
    [listEntities, workflowTemplateId, viewTemplate, profile],
  );

  const totalCardCount = listEntities?.list.totalResults ?? 0;

  const childCardCounts = useMemo(() => {
    const counts = new Map<string, number>();
    listEntities?.list.inverseRelatedCardCounts?.forEach(({ cardId, count }) => {
      counts.set(cardId, count);
    });
    return counts;
  }, [listEntities]);

  return useMemo(() => {
    return {
      entities,
      entityVariables,
      totalCardCount,
      childCardCounts,
      hasMoreCards,
      fetchMore: fetchMoreCards,
      refetch,
      canUpdateCard,
      isCardsLoading: isEntitiesLoading && isNil(listEntities),
      isMoreCardsLoading: isLoadingMore,
      onSearch: _ => {},
      onReorder: (_, __) => {},
      errors: [],
    };
  }, [
    entities,
    entityVariables,
    totalCardCount,
    childCardCounts,
    fetchMoreCards,
    canUpdateCard,
    hasMoreCards,
    isEntitiesLoading,
    isLoadingMore,
    listEntities,
    refetch,
  ]);
}

type GetFilterComponentProps = FilterSelectionState & { prepareForMutation?: boolean };

// Every sibling one-of branch, nulled out. Used when wrapping a nested filter below - the rest of
// this file spells the nulls out inline per branch, but `matchesRelatedEntity` is the only field
// set here so listing them once is clearer than repeating nine nulls.
const EMPTY_FILTER_ONE_OF: Omit<API.FilterByComponentFragment['value'], 'matchesRelatedEntity'> = {
  stringWildcard: null,
  containsNumber: null,
  containsString: null,
  dateIs: null,
  dateRange: null,
  dateTokenIs: null,
  numberIs: null,
  isEmpty: null,
  containsCurrentUser: null,
  booleanIs: null,
};

/**
 * Builds the `FilterByComponent` sent to the API for a single filter selection.
 *
 * `scalarKey` is attached here rather than inside each per-type branch below: it is a property of
 * the component being filtered (see `getFilterScalarKey`) and never varies with the value picked,
 * so resolving it once keeps those branches concerned only with the `FilterOneOf` value. A
 * cross-workflow filter names two components and so resolves one for each - see below.
 */
export function getFilterComponent(
  props: GetFilterComponentProps,
): API.FilterByComponentFragment | null {
  const componentFilter = getComponentFilterValue(props);
  if (isNil(componentFilter)) {
    return null;
  }
  const inner = { ...componentFilter, scalarKey: getFilterScalarKey(props.filter) };
  const relatedEntity = props.filter?.relatedEntity;
  if (isNil(relatedEntity)) {
    return inner;
  }
  // A cross-workflow filter: the filter we just built targets a component on the *related*
  // workflow, so it moves inside `matchesRelatedEntity` and the outer componentId becomes the
  // related-card component it is reached through. `type` is left unset, like every other variant
  // here - the GraphQL input default supplies it.
  //
  // Both levels carry their own `scalarKey`, each resolved from the component that level names.
  // The outer one is always a RELATED_CARD - that is what having a `relatedEntity` means - so it
  // resolves the same key a filter on that component directly would, which is how the traversal
  // reaches the same index.
  return {
    componentId: relatedEntity.componentId,
    scalarKey: getFilterScalarKey({ type: 'RELATED_CARD' }),
    value: {
      ...EMPTY_FILTER_ONE_OF,
      matchesRelatedEntity: { filterByComponent: inner },
    },
  };
}

function getComponentFilterValue({
  filter,
  selection,
  prepareForMutation = true,
  negate = false,
}: GetFilterComponentProps): API.FilterByComponentFragment | null {
  if (isNil(filter)) {
    return null;
  }
  // The component's own type, unless a scalar representation reads as something else - see
  // `getFilterValueType`. Which `FilterOneOf` branch to build follows the value being compared,
  // not the component it came from.
  switch (getFilterValueType(filter)) {
    case 'BOOLEAN': {
      if (!isFilterBooleanSelection(selection)) {
        return null;
      }
      return {
        componentId: filter.id,
        value: {
          // Always equality: a yes/no filter has no other comparison to make, so no operator is
          // surfaced (see `BooleanSelections`).
          booleanIs: { operator: 'EQ', value: selection.value },
          isEmpty: null,
          stringWildcard: null,
          containsNumber: null,
          containsString: null,
          dateIs: null,
          dateRange: null,
          dateTokenIs: null,
          numberIs: null,
          containsCurrentUser: null,
        },
      };
    }
    case 'TAG':
    case 'RELATED_CARD': {
      const filterValues = (selection as FilterTagSelection[])
        .filter(s => s.value !== 'empty')
        .filter(s => !isNil(s.value))
        .map(s => s.value);
      let containsString: NonNullable<
        API.FilterByComponentFragment['value']['containsString']
      > | null = null;
      if (!isEmpty(filterValues)) {
        containsString = {
          values: filterValues,
          negate,
        };
      }
      const emptyValue = !isNil((selection as FilterTagSelection[]).find(s => s.value === 'empty'));
      return {
        componentId: filter.id,
        value: {
          containsString,
          isEmpty: emptyValue
            ? {
                value: emptyValue,
              }
            : null,
          stringWildcard: null,
          containsNumber: null,
          dateIs: null,
          dateRange: null,
          dateTokenIs: null,
          numberIs: null,
          containsCurrentUser: null,
          booleanIs: null,
        },
      };
    }
    case 'PERSON': {
      const emptyValue = !isNil(
        (selection as FilterPersonSelection[]).find(s => s.value === 'empty'),
      );
      const currentUser = !isNil(
        (selection as FilterPersonSelection[]).find(s => s.value === 'currentUser'),
      );
      const filterValues =
        !emptyValue && !currentUser
          ? (selection as FilterPersonSelection[])
              .filter(s => s.value !== 'empty' && s.value !== 'currentUser')
              .map(s => Number(s.value))
          : null;
      let containsNumber: NonNullable<
        API.FilterByComponentFragment['value']['containsNumber']
      > | null = null;
      if (!isNil(filterValues) && !isEmpty(filterValues)) {
        containsNumber = {
          values: filterValues,
          negate,
        };
      }
      return {
        componentId: filter.id,
        value: {
          containsNumber,
          isEmpty: emptyValue
            ? {
                value: emptyValue,
              }
            : null,
          stringWildcard: null,
          containsString: null,
          dateIs: null,
          dateRange: null,
          dateTokenIs: null,
          numberIs: null,
          containsCurrentUser: currentUser ? { value: currentUser } : null,
          booleanIs: null,
        },
      };
    }
    case 'TEXT': {
      const isSelectionEmpty = (selection as FilterTextSelection)?.operator === 'empty';
      return {
        componentId: filter.id,
        value: {
          stringWildcard: isSelectionEmpty
            ? null
            : {
                value: (selection as FilterTextSelection).value ?? '',
              },
          isEmpty: isSelectionEmpty ? { value: true } : null,
          containsNumber: null,
          containsString: null,
          dateIs: null,
          dateRange: null,
          dateTokenIs: null,
          numberIs: null,
          containsCurrentUser: null,
          booleanIs: null,
        },
      };
    }
    case 'DATE': {
      const dateSelection = selection as FilterDateSelection;
      const isToday = dateSelection?.operator === 'today';
      const isRelative = isRelativeDateSelection(dateSelection);
      let dateRange: NonNullable<API.FilterByComponentFragment['value']['dateRange']> | null = null;

      if (!isRelative && operatorsThatNeedDates.includes(dateSelection?.operator)) {
        dateRange = getFilterDateRange(dateSelection);
      }

      const todayDateProps = getTodayDateFilters({
        hasTodayToken: isToday,
        convertToDateRange: !prepareForMutation,
      });

      // `duration` is already signed (negative = before today, positive =
      // after today), so it is passed through unchanged.
      const relativeDateProps = getRelativeDateFilters({
        isRelative,
        comparisonOperator: dateSelection?.comparisonOperator,
        duration: dateSelection?.duration,
        convertToDateRange: !prepareForMutation,
      });

      return {
        componentId: filter.id,
        value: {
          dateRange,
          ...todayDateProps,
          ...relativeDateProps,
          isEmpty: dateSelection?.operator === 'empty' ? { value: true } : null,
          stringWildcard: null,
          containsNumber: null,
          containsString: null,
          numberIs: null,
          containsCurrentUser: null,
          booleanIs: null,
        },
      };
    }
    case 'NUMBER': {
      const operator = (selection as FilterNumberSelection).operator;
      return {
        componentId: filter.id,
        value: {
          numberIs:
            operator !== 'empty'
              ? {
                  operator,
                  value: (selection as FilterNumberSelection).value,
                }
              : null,
          isEmpty: operator === 'empty' ? { value: true } : null,
          stringWildcard: null,
          containsNumber: null,
          containsString: null,
          dateIs: null,
          dateRange: null,
          dateTokenIs: null,
          containsCurrentUser: null,
          booleanIs: null,
        },
      };
    }
    default: {
      return null;
    }
  }
}

const operatorsThatNeedDates: FilterDateOperator[] = ['exactly', 'between', 'startsOn', 'endsOn'];
const operatorsThatNeedStartDate: FilterDateOperator[] = ['exactly', 'between', 'startsOn'];
const operatorsThatNeedEndDate: FilterDateOperator[] = ['between', 'endsOn'];

// `startsOn`/`endsOn` become relative when "keep relative to today" is on,
// persisting as a single relative bound (`dateIs.relativeDate`).
function isRelativeDateSelection(selection: FilterDateSelection | undefined): boolean {
  return (
    !!selection?.isRelative &&
    (selection.operator === 'startsOn' || selection.operator === 'endsOn')
  );
}

function getFilterDateRange(
  dateSelection: FilterDateSelection,
): NonNullable<API.FilterByComponentFragment['value']['dateRange']> | null {
  if (operatorsThatNeedDates.includes(dateSelection?.operator)) {
    return {
      after: operatorsThatNeedStartDate.includes(dateSelection.operator)
        ? (dateSelection.after as string)
        : null,
      before: getFilterDateRangeEnd(dateSelection),
    };
  } else {
    return null;
  }
}

function getFilterDateRangeEnd(dateSelection: FilterDateSelection): string | null {
  if (operatorsThatNeedEndDate.includes(dateSelection.operator)) {
    return dateSelection.before ?? null;
  }
  if (dateSelection.operator === 'exactly') {
    return moment(dateSelection.after).endOf('day').toISOString() as string;
  }
  return null;
}

export function getTodayDateFilters({
  hasTodayToken,
  convertToDateRange,
}: {
  hasTodayToken: boolean;
  convertToDateRange: boolean;
}): {
  dateRange?: NonNullable<API.FilterByComponentFragment['value']['dateRange']>;
  dateTokenIs: NonNullable<API.FilterByComponentFragment['value']['dateTokenIs']> | null;
} {
  if (hasTodayToken && convertToDateRange) {
    return {
      dateRange: {
        after: moment().startOf('day').toISOString(),
        before: moment().endOf('day').toISOString(),
      },
      dateTokenIs: null,
    };
  }
  if (hasTodayToken) {
    return {
      dateTokenIs: { value: 'TODAY' },
    };
  }
  return {
    dateTokenIs: null,
  };
}

export function getRelativeDateFilters({
  isRelative,
  comparisonOperator,
  duration,
  convertToDateRange,
}: {
  isRelative: boolean;
  comparisonOperator?: API.ComparisonOperator;
  duration?: number;
  convertToDateRange: boolean;
}): {
  dateRange?: NonNullable<API.FilterByComponentFragment['value']['dateRange']>;
  dateIs: NonNullable<API.FilterByComponentFragment['value']['dateIs']> | null;
} {
  const isValidRelativeDate = isRelative && !isNil(comparisonOperator) && !isNil(duration);

  /* Notes
  Specified day is (+/-) duration days from today

  GT/GTE - after
    - !E is exclusive. start of day after specified day.
    - E is inclusive. start of specified day.
  LT/LTE - before
    - !E is exclusive. end of day before specified day.
    - E is inclusive. end of specified day
  */
  if (isValidRelativeDate && convertToDateRange) {
    const specifiedDay = moment().add(duration, 'days');
    let after: string | null = null;
    let before: string | null = null;
    if (comparisonOperator === 'GT') {
      after = specifiedDay.add(1, 'day').startOf('day').toISOString();
    }
    if (comparisonOperator === 'GTE') {
      after = specifiedDay.startOf('day').toISOString();
    }
    if (comparisonOperator === 'LT') {
      before = specifiedDay.subtract(1, 'day').endOf('day').toISOString();
    }
    if (comparisonOperator === 'LTE') {
      before = specifiedDay.endOf('day').toISOString();
    }

    return {
      dateRange: {
        after,
        before,
      },
      dateIs: null,
    };
  }

  if (isValidRelativeDate) {
    return {
      dateIs: {
        operator: comparisonOperator,
        relativeDate: {
          duration,
          timeUnit: 'DAYS',
        },
      },
    };
  }

  return {
    dateIs: null,
  };
}

export function getSortComponent({ sort, direction }: SortSelectionState): API.SortByComponent {
  return {
    componentId: sort.id,
    direction,
  };
}

interface GetEntitiesProps {
  listEntities: API.ListWorkflowEntitiesQuery | undefined;
  workflowTemplateId: string;
  viewTemplate: API.ViewTemplateFragment | undefined;
  currentUser?: User;
}

export function getEntities({
  listEntities,
  workflowTemplateId,
  viewTemplate,
  currentUser,
}: GetEntitiesProps): Entity[] {
  const entityList = listEntities?.list.entities;
  const refs = listEntities?.list.refs;
  if (!entityList || !refs || !viewTemplate) {
    return [];
  }

  return entityList
    .filter(isEntity)
    .map(entity => new CardEntity({ entity, workflowTemplateId, refs, viewTemplate, currentUser }));
}

export function isEntity(
  entity: NonNullable<API.ListWorkflowEntitiesQuery['list']['entities']>[number] | null,
): entity is NonNullable<API.ListWorkflowEntitiesQuery['list']['entities']>[number] {
  return !isNil(entity);
}
